import { useEffect, useRef, useState } from "react";

// Hook to determine if the text content of a div is truncated (i.e., overflows its container)
// returns a ref to be attached to the div and a boolean indicating if it's truncated
export const useIsTruncated = () => {
    const ref = useRef<HTMLDivElement>(null);
    const [isTruncated, setIsTruncated] = useState(false);

    useEffect(() => {
        const element = ref.current;
        if (!element) {
            setIsTruncated(false);
            return;
        }

        let retryCount = 0;
        const MAX_RETRIES = 10;

        const checkTruncation = () => {
            // Wait for element to have dimensions
            if (element.clientWidth === 0 || element.scrollWidth === 0) {
                if (retryCount < MAX_RETRIES) {
                    retryCount++;
                    setTimeout(checkTruncation, 50);
                }
                return;
            }

            const range = document.createRange();
            range.selectNodeContents(element);
            const textWidth = range.getBoundingClientRect().width;
            range.detach();
            const containerWidth = element.getBoundingClientRect().width;
            setIsTruncated(textWidth > containerWidth);
        };

        // Initial check
        setTimeout(checkTruncation, 100);

        // Set up resize observer
        let resizeObserver: ResizeObserver | null = null;
        if (typeof ResizeObserver !== "undefined") {
            resizeObserver = new ResizeObserver(() => {
                setTimeout(checkTruncation, 10);
            });
            resizeObserver.observe(element);
        } else {
            // Fallback for older browsers
            const handleResize = () => setTimeout(checkTruncation, 50);
            window.addEventListener("resize", handleResize);
            return () => window.removeEventListener("resize", handleResize);
        }

        return () => resizeObserver?.disconnect();
    }, []);

    return { ref, isTruncated };
};
